import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  RefreshCw,
  X,
  User,
  Calendar,
  AlertCircle,
  ChevronDown,
  Filter,
  Shield
} from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatTime = (iso) => {
  if (!iso) return '--:--';
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const calcHours = (inIso, outIso) => {
  if (!inIso || !outIso) return null;
  const diff = (new Date(outIso) - new Date(inIso)) / (1000 * 60 * 60);
  return diff > 0 ? diff.toFixed(1) : null;
};

const STATUS_BADGE = {
  pending: 'bg-[#FEF3C7] text-[#92400E] border border-[#F59E0B]',
  approved: 'bg-[#D1FAE5] text-[#065F46] border border-[#10B981]',
  rejected: 'bg-[#FEE2E2] text-[#991B1B] border border-[#EF4444]'
};

const STATUS_ICON = { pending: Clock, approved: CheckCircle, rejected: XCircle };

const StatusBadge = ({ status }) => {
  const Icon = STATUS_ICON[status] || Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[status] || 'bg-gray-100 text-gray-700 border border-gray-300'}`}>
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
};

const buildQS = ({ status, userId, startDate, endDate }) => {
  const p = new URLSearchParams();
  if (status && status !== 'all') p.set('status', status);
  if (userId) p.set('userId', userId);
  if (startDate) p.set('startDate', startDate);
  if (endDate) p.set('endDate', endDate);
  const s = p.toString();
  return s ? `?${s}` : '';
};

// ─── Stats Cards ──────────────────────────────────────────────────────────────

const StatsCards = ({ stats, loading, activeStatus, onCardClick }) => {
  const cards = [
    { label: 'Total', filter: 'all', value: stats?.total, icon: Filter, cls: 'from-blue-50 to-blue-100 border-blue-200', valCls: 'text-blue-700' },
    { label: 'Pending', filter: 'pending', value: stats?.pending, icon: Clock, cls: 'from-yellow-50 to-yellow-100 border-yellow-200', valCls: 'text-yellow-700' },
    { label: 'Approved', filter: 'approved', value: stats?.approved, icon: CheckCircle, cls: 'from-green-50 to-green-100 border-green-200', valCls: 'text-green-700' },
    { label: 'Rejected', filter: 'rejected', value: stats?.rejected, icon: XCircle, cls: 'from-red-50 to-red-100 border-red-200', valCls: 'text-red-700' }
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
      {cards.map(({ label, filter, value, icon: Icon, cls, valCls }) => {
        const isActive = activeStatus === filter;
        return (
          <button
            key={filter}
            onClick={() => onCardClick(filter)}
            className={`text-left p-4 rounded-2xl border bg-gradient-to-br ${cls} transition-all duration-150 ${isActive ? 'ring-2 ring-offset-2 ring-indigo-400 shadow-lg shadow-indigo-100' : 'hover:shadow-[0_10px_24px_rgba(15,23,42,0.08)]'}`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">{label}</p>
              <Icon className={`h-4 w-4 ${valCls} opacity-70`} />
            </div>
            <p className={`mt-2 text-3xl font-semibold tracking-tight ${valCls}`}>{loading ? '—' : (value ?? '—')}</p>
            {isActive && <p className="mt-1 text-xs font-medium text-indigo-500">Active filter</p>}
          </button>
        );
      })}
    </div>
  );
};

// ─── Filters Bar ──────────────────────────────────────────────────────────────

const FiltersBar = ({ filters, onChange, onApply, onReset, loading }) => (
  <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
    <div className="flex flex-wrap items-end gap-3">
      {/* Status */}
      <div className="flex flex-col w-full sm:w-auto sm:min-w-[140px]">
        <label className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
        <div className="relative">
          <select
            value={filters.status}
            onChange={(e) => onChange('status', e.target.value)}
            className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-8 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Employee ID */}
      <div className="flex flex-col w-full sm:w-auto sm:min-w-[180px]">
        <label className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Employee (userId)</label>
        <input
          type="text"
          placeholder="MongoDB user ID"
          value={filters.userId}
          onChange={(e) => onChange('userId', e.target.value)}
          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
        />
      </div>

      {/* Start Date */}
      <div className="flex flex-col w-full sm:w-auto sm:min-w-[150px]">
        <label className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">From</label>
        <input
          type="date"
          value={filters.startDate}
          onChange={(e) => onChange('startDate', e.target.value)}
          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
        />
      </div>

      {/* End Date */}
      <div className="flex flex-col w-full sm:w-auto sm:min-w-[150px]">
        <label className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">To</label>
        <input
          type="date"
          value={filters.endDate}
          onChange={(e) => onChange('endDate', e.target.value)}
          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-2 pb-0.5">
        <button
          onClick={onApply}
          disabled={loading}
          className="inline-flex items-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-50"
        >
          <Filter className="h-4 w-4 mr-1.5" />
          Apply
        </button>
        <button
          onClick={onReset}
          disabled={loading}
          className="inline-flex items-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          Reset
        </button>
      </div>
    </div>
  </div>
);

// ─── Requests Table ───────────────────────────────────────────────────────────

const RequestsTable = ({ requests, loading, onReview, actionLoading }) => {
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white/90 p-12 flex justify-center shadow-sm">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (!requests.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white/90 p-12 text-center shadow-sm">
        <AlertCircle className="h-10 w-10 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 font-medium">No requests found for selected filters.</p>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white/90 shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50/90">
            <tr>
              {['Employee', 'Attendance Date', 'Original → Requested', 'Reason', 'Submitted', 'Status', 'Action'].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {requests.map((req) => (
              <tr key={req._id} className="transition-colors hover:bg-slate-50/70">
                {/* Employee */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center flex-shrink-0 ring-1 ring-indigo-100">
                      <User className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{req.userId?.name || '—'}</p>
                      <p className="text-xs text-gray-400">{req.userId?.employeeId || '—'}</p>
                    </div>
                  </div>
                </td>

                {/* Attendance Date */}
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                    {formatDate(req.attendanceDate)}
                  </div>
                </td>

                {/* Times */}
                <td className="px-4 py-3 text-xs whitespace-nowrap">
                  <div className="space-y-0.5">
                    <p className="text-slate-400">
                      <span className="font-medium text-slate-600">{formatTime(req.originalCheckIn)}</span>
                      {' → '}
                      <span className="font-semibold text-indigo-600">{formatTime(req.requestedCheckIn)}</span>
                      <span className="ml-1 text-slate-400">(in)</span>
                    </p>
                    <p className="text-slate-400">
                      <span className="font-medium text-slate-600">{formatTime(req.originalCheckOut)}</span>
                      {' → '}
                      <span className="font-semibold text-indigo-600">{formatTime(req.requestedCheckOut)}</span>
                      <span className="ml-1 text-slate-400">(out)</span>
                    </p>
                  </div>
                </td>

                {/* Reason */}
                <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px]">
                  <p className="truncate" title={req.reason}>{req.reason || '—'}</p>
                </td>

                {/* Submitted */}
                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                  {formatDateTime(req.requestedAt)}
                </td>

                {/* Status */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <StatusBadge status={req.status} />
                </td>

                {/* Action */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <button
                    onClick={() => onReview(req._id)}
                    disabled={!!actionLoading[req._id]}
                    className="inline-flex items-center rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 transition hover:bg-indigo-100 disabled:opacity-50"
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Reject Reason Input (inside drawer) ─────────────────────────────────────

const RejectPanel = ({ onConfirm, onCancel, loading }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-gray-700">Rejection Reason <span className="text-gray-400 font-normal">(optional, max 500)</span></p>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={500}
        rows={3}
        placeholder="Provide a reason for rejection..."
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-400 resize-none"
      />
      <p className="text-right text-xs text-gray-400">{reason.length}/500</p>
      <div className="flex gap-2">
        <button
          onClick={() => onConfirm(reason.trim())}
          disabled={loading}
          className="flex-1 flex items-center justify-center px-4 py-2 bg-[#EF4444] text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50 transition-colors active:scale-95"
        >
          {loading ? <LoadingSpinner size="sm" /> : 'Confirm Rejection'}
        </button>
        <button
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Detail Drawer ────────────────────────────────────────────────────────────

const DetailDrawer = ({ requestId, onClose, onApproveSuccess, onRejectSuccess, actionLoading, setActionLoading }) => {
  const [detail, setDetail] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(true);
  const [drawerError, setDrawerError] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Mount animation
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    if (!requestId) return;
    setDrawerLoading(true);
    setDrawerError(false);
    setRejectMode(false);
    api
      .get(`/superadmin/attendance-requests/${requestId}`)
      .then((res) => setDetail(res.data?.request || res.data))
      .catch(() => setDrawerError(true))
      .finally(() => setDrawerLoading(false));
  }, [requestId]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const handleApprove = async () => {
    setActionLoading((p) => ({ ...p, [requestId]: 'approve' }));
    try {
      await api.put(`/superadmin/attendance-requests/${requestId}/approve`);
      const newStatus = detail?.attendanceId?.status;
      toast.success(`Request approved.${newStatus ? ` Attendance updated to ${newStatus}.` : ''}`);
      onApproveSuccess();
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve request.');
    } finally {
      setActionLoading((p) => ({ ...p, [requestId]: null }));
    }
  };

  const handleReject = async (reason) => {
    setActionLoading((p) => ({ ...p, [requestId]: 'reject' }));
    try {
      await api.put(`/superadmin/attendance-requests/${requestId}/reject`, reason ? { rejectionReason: reason } : {});
      toast.success('Request rejected.');
      onRejectSuccess();
      handleClose();
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.errors?.[0]?.msg || errData?.message || 'Failed to reject request.';
      toast.error(msg);
    } finally {
      setActionLoading((p) => ({ ...p, [requestId]: null }));
    }
  };

  const isActing = !!actionLoading[requestId];
  const isPending = detail?.status === 'pending';

  // Computed hours for comparison table
  const origHours = calcHours(detail?.originalCheckIn, detail?.originalCheckOut);
  const reqHours = calcHours(detail?.requestedCheckIn, detail?.requestedCheckOut);
  const attendanceObjHours = detail?.attendanceId?.totalHours;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black bg-opacity-30 transition-opacity duration-250"
        style={{ opacity: visible ? 1 : 0 }}
        onClick={handleClose}
      />

      {/* Drawer panel */}
      <div
        className="fixed top-0 right-0 z-50 h-full w-full max-w-xl bg-white shadow-2xl flex flex-col overflow-hidden"
        style={{
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 250ms ease-out'
        }}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-indigo-700">
          <div className="flex items-center gap-2 text-white">
            <Shield className="h-5 w-5 opacity-80" />
            <h3 className="text-base font-semibold">Super Admin Review</h3>
          </div>
          <button onClick={handleClose} className="text-white opacity-70 hover:opacity-100 transition-opacity">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Drawer body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {drawerLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {drawerError && !drawerLoading && (
            <div className="text-center py-10">
              <AlertCircle className="h-8 w-8 mx-auto text-red-400 mb-2" />
              <p className="text-gray-500">Failed to load request details.</p>
            </div>
          )}

          {detail && !drawerLoading && (
            <>
              {/* Status + badge */}
              <div className="flex items-center justify-between">
                <StatusBadge status={detail.status} />
                {detail.status === 'pending' && (
                  <span className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-0.5 font-medium">
                    Awaiting Super Admin Review
                  </span>
                )}
              </div>

              {/* Employee info */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-3">Employee</p>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{detail.userId?.name || '—'}</p>
                    <p className="text-sm text-gray-500">{detail.userId?.email || '—'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      ID: {detail.userId?.employeeId || '—'}
                      {detail.userId?.organization ? ` · ${detail.userId.organization}` : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Attendance date */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Attendance Date</p>
                <div className="flex items-center gap-2 text-gray-800">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{formatDate(detail.attendanceDate)}</span>
                </div>
              </div>

              {/* Time comparison table */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Time Comparison</p>
                <div className="rounded-xl border border-gray-200 overflow-hidden text-sm">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500"></th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Check-in</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Check-out</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Hours</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr className="bg-white">
                        <td className="px-3 py-2 font-medium text-gray-600 whitespace-nowrap">Current (system)</td>
                        <td className="px-3 py-2 text-gray-700">{formatTime(detail.originalCheckIn)}</td>
                        <td className="px-3 py-2 text-gray-700">{formatTime(detail.originalCheckOut)}</td>
                        <td className="px-3 py-2 text-gray-600">
                          {attendanceObjHours != null
                            ? `${attendanceObjHours} hrs`
                            : origHours ? `${origHours} hrs` : '—'}
                          {detail.attendanceId?.status && (
                            <span className="ml-1 text-xs text-gray-400 capitalize">({detail.attendanceId.status.replace('-', ' ')})</span>
                          )}
                        </td>
                      </tr>
                      <tr className="bg-indigo-50">
                        <td className="px-3 py-2 font-semibold text-indigo-700 whitespace-nowrap">Requested</td>
                        <td className="px-3 py-2 font-bold text-indigo-800">{formatTime(detail.requestedCheckIn)}</td>
                        <td className="px-3 py-2 font-bold text-indigo-800">{formatTime(detail.requestedCheckOut)}</td>
                        <td className="px-3 py-2 text-indigo-700">
                          {reqHours ? `${reqHours} hrs` : '—'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Reason */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Reason</p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3 border border-gray-200 whitespace-pre-wrap leading-relaxed">
                  {detail.reason || '—'}
                </p>
              </div>

              {/* Submitted at */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Submitted At</p>
                  <p className="text-sm text-gray-600">{formatDateTime(detail.requestedAt)}</p>
                </div>
                {detail.requestedIp && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase mb-1">IP Address</p>
                    <p className="text-sm text-gray-500 font-mono">{detail.requestedIp}</p>
                  </div>
                )}
              </div>

              {/* Admin decision */}
              {detail.status === 'approved' && (
                <div className="bg-[#D1FAE5] border border-[#10B981] rounded-xl p-4">
                  <p className="text-xs font-semibold text-[#065F46] uppercase mb-2">Approved</p>
                  <p className="text-sm text-[#065F46]">
                    By: <span className="font-semibold">{detail.approvedBy?.name || '—'}</span>
                  </p>
                  <p className="text-sm text-[#065F46] mt-1">At: {formatDateTime(detail.approvedAt)}</p>
                </div>
              )}
              {detail.status === 'rejected' && (
                <div className="bg-[#FEE2E2] border border-[#EF4444] rounded-xl p-4">
                  <p className="text-xs font-semibold text-[#991B1B] uppercase mb-2">Rejected</p>
                  <p className="text-sm text-[#991B1B]">At: {formatDateTime(detail.approvedAt)}</p>
                  {detail.rejectionReason && (
                    <p className="text-sm text-[#991B1B] mt-2">
                      Reason: <span className="font-semibold">{detail.rejectionReason}</span>
                    </p>
                  )}
                </div>
              )}

              {/* Reject mode panel */}
              {rejectMode && isPending && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <RejectPanel
                    onConfirm={handleReject}
                    onCancel={() => setRejectMode(false)}
                    loading={isActing}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Drawer footer actions */}
        {detail && isPending && !rejectMode && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex gap-3">
            <button
              onClick={handleApprove}
              disabled={isActing}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-[#10B981] text-white rounded-lg text-sm font-semibold hover:bg-[#059669] disabled:opacity-50 transition-colors active:scale-95"
            >
              {actionLoading[requestId] === 'approve' ? <LoadingSpinner size="sm" /> : <><CheckCircle className="h-4 w-4 mr-1.5" />Approve</>}
            </button>
            <button
              onClick={() => setRejectMode(true)}
              disabled={isActing}
              className="flex-1 flex items-center justify-center px-4 py-2 border-2 border-[#EF4444] text-[#EF4444] rounded-lg text-sm font-semibold hover:bg-[#FEE2E2] disabled:opacity-50 transition-colors active:scale-95"
            >
              <XCircle className="h-4 w-4 mr-1.5" />
              Reject
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const INITIAL_FILTERS = { status: 'pending', userId: '', startDate: '', endDate: '' };

const SuperAdminAttendanceRequests = ({ onSidebarPendingRefresh }) => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [actionLoading, setActionLoading] = useState({});
  const [drawerRequestId, setDrawerRequestId] = useState(null);

  // page fade-in
  const [pageVisible, setPageVisible] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setPageVisible(true)); }, []);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get('/superadmin/attendance-requests/stats/summary');
      setStats(res.data);
    } catch {
      // non-critical
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchRequests = useCallback(async (currentFilters) => {
    setTableLoading(true);
    try {
      const qs = buildQS(currentFilters);
      const res = await api.get(`/superadmin/attendance-requests${qs}`);
      setRequests(res.data?.requests || res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load requests.');
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchRequests(appliedFilters); }, [appliedFilters, fetchRequests]);

  const handleFilterChange = (key, value) => setFilters((p) => ({ ...p, [key]: value }));
  const handleApplyFilters = () => setAppliedFilters({ ...filters });
  const handleResetFilters = () => { setFilters(INITIAL_FILTERS); setAppliedFilters(INITIAL_FILTERS); };
  const handleCardClick = (status) => {
    const updated = { ...INITIAL_FILTERS, status };
    setFilters(updated);
    setAppliedFilters(updated);
  };

  const handleActionSuccess = () => {
    fetchStats();
    fetchRequests(appliedFilters);
    if (onSidebarPendingRefresh) {
      onSidebarPendingRefresh();
    }
  };

  return (
    <div
      className="space-y-6"
      style={{ opacity: pageVisible ? 1 : 0, transition: 'opacity 150ms ease' }}
    >
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-3xl border border-slate-200 bg-gradient-to-r from-white to-slate-50/80 p-5 shadow-sm sm:p-6">
        <div className="max-w-3xl">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            <Shield className="h-5 w-5 text-indigo-500" />
            Regularization Requests
          </h1>
          <p className="mt-1 text-sm leading-6 text-slate-500">High-frequency attendance adjustments waiting for review, filtered and prioritized in one place.</p>
        </div>
        <button
          onClick={() => { fetchStats(); fetchRequests(appliedFilters); }}
          className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-600 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 mr-1.5 ${tableLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <StatsCards
        stats={stats}
        loading={statsLoading}
        activeStatus={appliedFilters.status || 'all'}
        onCardClick={handleCardClick}
      />

      {/* Filters */}
      <FiltersBar
        filters={filters}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        loading={tableLoading}
      />

      {/* Table */}
      <RequestsTable
        requests={requests}
        loading={tableLoading}
        actionLoading={actionLoading}
        onReview={(id) => setDrawerRequestId(id)}
      />

      {/* Detail Drawer */}
      {drawerRequestId && (
        <DetailDrawer
          requestId={drawerRequestId}
          onClose={() => setDrawerRequestId(null)}
          onApproveSuccess={handleActionSuccess}
          onRejectSuccess={handleActionSuccess}
          actionLoading={actionLoading}
          setActionLoading={setActionLoading}
        />
      )}
    </div>
  );
};

export default SuperAdminAttendanceRequests;
